import axios from 'axios';
import { normalize } from 'normalizr';
import { toCollectionsModel } from '../../mappers/collections/toCollectionsModel';
import { toPostModel } from '../../mappers/posts/toPostModel';
import { toPostPersistenceDto } from '../../mappers/posts/toPostPersistenceDto';
import { toPostStatisticModel } from '../../mappers/posts/toPostStatisticModel';
import { canEditBusiness } from '../../utils/accessService';
import { getFileExtension } from '../../utils/getFileExtension';
import { share } from '../../utils/share';
import { collectionsSchema } from '../collections/schema';
import { CollectionModel, CollectionsDataModel } from '../collections/types';
import * as mediaService from '../media/service';
import { MediaModel } from '../media/types';
import { UserModel } from '../user/types';
import { PostSource, PostStatus } from './constants';
import { postSchema } from './schema';
import type {
    PostDataModel,
    PostDto,
    PostModel,
    PostPersistenceModel,
    ReportReasons,
} from './types';
import type { EventSource } from '../../utils/analyticsService';
import type { BusinessModel } from '../business/types';
import type { CategoryDataModel } from '../categories/types';

class PostMediaUploadError extends Error {
    constructor(message: string = 'Не удалось загрузить медиа') {
        super(message);
        this.name = 'FileUploadError';
    }
}

function getFeedEndpoint(
    businessId?: string | null,
    collectionId?: string | null,
    query?: string | null,
) {
    if (businessId) {
        return `/feed/${businessId}`;
    }
    if (collectionId) {
        return `/collections/feed/${collectionId}`;
    }
    if (query || query === '') {
        return '/feed/search';
    }

    return '/feed';
}

export async function loadFeed(filters: {
    amount: number;
    categoryId?: string | null;
    businessId?: string | null;
    collectionId?: string | null;
    statuses?: PostStatus[];
    data_sources?: PostSource[];
    query?: string | null;
    push_payload?: string | null;
    first_post_id?: string | null;
    no_log?: string | null;
    next?: string;
}): Promise<{
    next: string;
    posts: Array<PostDataModel['id']>;
    records: {
        posts: PostDataModel[];
        business: BusinessModel[];
        categories: CategoryDataModel[];
    };
}> {
    const {
        businessId,
        collectionId,
        statuses = [],
        data_sources = [],
        query,
        ...params
    } = filters;

    const feedUrl = getFeedEndpoint(businessId, collectionId, query);

    const response = await axios.get(feedUrl, {
        params: {
            ...params,
            query: query,
            statuses: statuses.length > 0 ? JSON.stringify(statuses) : undefined,
            data_sources: data_sources.length > 0 ? JSON.stringify(data_sources) : undefined,
        },
    });

    const normalized = normalize(
        {
            ...response.data,
            posts: response.data.posts.map(toPostModel),
        },
        {
            posts: [postSchema],
        },
    );

    return {
        next: normalized.result.next,
        posts: normalized.result.posts,
        records: {
            posts: Object.values(normalized.entities.posts ?? {}),
            business: Object.values(normalized.entities.business ?? {}),
            categories: Object.values(normalized.entities.categories ?? {}),
        },
    };
}

export async function removePosts(identifiers: string[]): Promise<void> {
    return await axios.delete('/posts/', {
        data: {
            post_ids: identifiers,
        },
    });
}

export async function loadPostById(targetId: string): Promise<{
    records: {
        posts: PostDataModel[];
        business: BusinessModel[];
        categories: CategoryDataModel[];
    };
}> {
    const response = await axios.get(`/posts/${targetId}`);
    const normalized = normalize({ post: toPostModel(response.data.post) }, { post: postSchema });

    return {
        records: {
            posts: Object.values(normalized.entities.posts ?? {}),
            business: Object.values(normalized.entities.business ?? {}),
            categories: Object.values(normalized.entities.categories ?? {}),
        },
    };
}

export async function persistPost(model: PostPersistenceModel): Promise<PostModel> {
    let response;
    if (model.id) {
        response = await axios.patch(`/posts/${model.id}`, toPostPersistenceDto(model));
    } else {
        response = await axios.post('/posts', toPostPersistenceDto(model));
    }

    return toPostModel(response.data.post);
}

export async function updatePostReaction({
    reactionState,
    model,
    params,
}: {
    reactionState: PostModel['reactionState'];
    model: PostModel;
    params: {
        postNumber: number;
        eventSource: EventSource;
        collectionId?: string;
    };
}): Promise<Pick<PostDto, 'reaction_state'>> {
    return await axios(`/posts/${model.id}/reaction`, {
        method: 'POST',
        params: {
            post_number: params.postNumber,
            event_source: params.eventSource,
            collection_id: params.collectionId,
        },
        data: {
            reaction_state: reactionState,
        },
    });
}

export async function sharePost(post: PostModel): Promise<ReturnType<typeof share>> {
    return await share({ url: post.shareUrl });
}

export async function reportPost(postId: string, reason: ReportReasons): Promise<void> {
    await axios.put(`/posts/${postId}/report`, {}, { params: { report_reason: reason } });
}

export async function bookmarkPost({
    model,
    collectionId,
}: {
    model: PostModel;
    collectionId?: string;
}): Promise<CollectionModel> {
    const response = model.saved
        ? await axios(`/favourites/${model.id}`, {
              method: 'DELETE',
              ...(collectionId ? { params: { collection_id: collectionId } } : {}),
          })
        : await axios('/favourites', {
              method: 'POST',
              data: {
                  post_id: model.id,
              },
          });

    return response.data.collections.map(toCollectionsModel);
}

export async function bulkUpdateStatus(
    models: PostModel[],
    status: PostStatus,
    user?: UserModel,
): Promise<void> {
    const invalidModels = models.filter((model) => !canChangeStatus(model, status, user));

    if (invalidModels.length > 0) {
        throw new Error('Недопустимый переход статуса');
    }

    await axios.patch(`/posts/status/${status}`, {
        post_ids: models.map((model) => model.id),
    });
}

const STATUS_FINITE_STATE_MACHINE: Record<PostStatus, PostStatus[]> = {
    [PostStatus.DRAFT]: [PostStatus.MODERATION, PostStatus.DELETED],
    [PostStatus.PUBLISHED]: [PostStatus.DELETED],
    [PostStatus.MODERATION]: [PostStatus.DELETED],
    [PostStatus.LIMITED]: [PostStatus.DELETED],
    [PostStatus.DECLINED]: [PostStatus.DELETED],
    [PostStatus.DELETED]: [],
};

export const canChangeStatus = (model: PostModel, status: PostStatus, user?: UserModel): boolean =>
    Boolean(user) &&
    canEditBusiness(model.business?.id, user) &&
    STATUS_FINITE_STATE_MACHINE[model.status ?? PostStatus.PUBLISHED].includes(status);

export const canRemove = (model: PostModel, user?: UserModel): boolean =>
    canChangeStatus(model, PostStatus.DELETED, user) || Boolean(model.ownersData?.isOwner);

export async function updatePostStatus(model: PostModel, status: PostStatus) {
    const availableStatuses = STATUS_FINITE_STATE_MACHINE[model.status ?? PostStatus.PUBLISHED];

    if (!availableStatuses.includes(status)) {
        // TODO: Завести кастомный класс ошибок
        throw new Error('Недопустимый переход статуса');
    }

    await axios.patch(`/posts/${model.id}/status/${status}`);
}

const MEDIA_RATIO_MIN = 9 / 16;
const MEDIA_RATIO_MAX = 16 / 9;
const VIDEO_DURATION_MAX = 180;
const VIDEO_SIZE_MAX = 200 * 1024 * 1024;
const IMAGE_SIZE_MAX = 15 * 1024 * 1024;

export async function uploadPostMedia(file: File): Promise<MediaModel> {
    const fileExtension = getFileExtension(file);

    if (file.type.startsWith('video')) {
        const meta = await mediaService.getVideoFileMeta(file);
        const ratio = meta.width / meta.height;

        if (meta.size > VIDEO_SIZE_MAX) {
            throw new PostMediaUploadError('Видео должно быть менее 200Мб');
        }

        if (meta.duration > VIDEO_DURATION_MAX) {
            throw new PostMediaUploadError('Видео должно быть менее 3 минут');
        }

        if (ratio > MEDIA_RATIO_MAX || ratio < MEDIA_RATIO_MIN) {
            throw new PostMediaUploadError('Недопустимое соотношение сторон видео');
        }

        const blob = await mediaService.convertToBlob(file);
        const media = await mediaService.uploadBlob({
            blob,
            fileExtension,
            fileType: 'video',
            mediaSizes: {
                width: meta.width,
                height: meta.height,
            },
        });

        return {
            ...media,
            type: 'video',
            width: meta.width,
            height: meta.height,
        };
    }

    if (file.type.startsWith('image')) {
        const meta = await mediaService.getImageFileMeta(file);
        const ratio = meta.width / meta.height;

        if (meta.size > IMAGE_SIZE_MAX) {
            throw new PostMediaUploadError('Изображение должно быть менее 15Мб');
        }

        if (ratio > MEDIA_RATIO_MAX || ratio < MEDIA_RATIO_MIN) {
            throw new PostMediaUploadError('Недопустимое соотношение сторон изображения');
        }

        const blob = await mediaService.convertToBlob(file);
        const media = await mediaService.uploadBlob({
            blob,
            fileExtension,
            fileType: 'image',
            mediaSizes: {
                width: meta.width,
                height: meta.height,
            },
        });

        return { ...media, type: 'image', width: meta.width, height: meta.height };
    }

    throw new PostMediaUploadError('Неподдерживаемый тип файла');
}

export async function loadPostStatistic(postId: string) {
    const response = await axios.get(`/posts/${postId}/stat`);

    return {
        stats: toPostStatisticModel(response.data.stats),
    };
}

export async function searchPostByUrl(url: string) {
    const response = await axios.post('/posts/parse_product', { url });

    return toPostModel(response.data.post);
}

interface SavePostAsProductPost {
    post_id: string;
    collection_id: string;
    media: {
        id: string;
        type: string;
    }[];
}

export async function savePostAsProduct(post: SavePostAsProductPost): Promise<{
    collections: {
        collections: Array<CollectionsDataModel['id']>;
        records: {
            collections: CollectionsDataModel[];
        };
    };
    post: {
        records: {
            posts: PostDataModel[];
            business: BusinessModel[];
            categories: CategoryDataModel[];
        };
    };
}> {
    const response = await axios.post('/posts/save_product', post);

    const normalizedPost = normalize(
        { post: toPostModel(response.data.post) },
        { post: postSchema },
    );
    const collections = response.data.collections || [];
    const normalizedCollections = normalize(
        {
            collections: collections.map(toCollectionsModel),
        },
        {
            collections: [collectionsSchema],
        },
    );

    return {
        collections: {
            collections: normalizedCollections.result.collections,
            records: {
                collections: Object.values<CollectionsDataModel>(
                    normalizedCollections.entities.collections ?? {},
                ),
            },
        },
        post: {
            records: {
                posts: Object.values(normalizedPost.entities.posts ?? {}),
                business: Object.values(normalizedPost.entities.business ?? {}),
                categories: Object.values(normalizedPost.entities.categories ?? {}),
            },
        },
    };
}
